/* eslint-disable import/no-unused-modules */
/* eslint-disable react/display-name */
import React, { useMemo } from 'react';
import { groupBy } from 'lodash';
import PropTypes from 'prop-types';
import Tippy from '@tippy.js/react';
import { IconAnomalyDetection, IconCondition, IconTrendPoorIndicator, IconTrendTolerableIndicator } from 'svgIcons/MotionPortalIcons';
import { translate } from 'helpers/translateHelper';
import { ASSET_TYPES, newConditionIndices } from 'helpers/constants';
import { conditionIndices } from './constants';
import { getAssetTypeId } from 'helpers/assetAndPowertrainFilterHelper';
// import KPIListViewComponent from './KPIListViewComponent'; // Assuming RenderKPIList is already optimized

const TrendViewComponent = React.memo(({ item, isLabelVisible, leftPanelSize }) => {
    const conditions = [
        conditionIndices.POOR,
        conditionIndices.TOLERABLE,
        conditionIndices.UNKNOWN,
        conditionIndices.GOOD,
        conditionIndices.UNSUPPORTED
    ];

    const getAnomalyDetectionKPI = item?.trend?.KPIs?.find(kpi => kpi.isAnomaly);
    const isDriveAsset = getAssetTypeId(item?.assetType) === ASSET_TYPES.DRIVE;
    const isMotorAsset = getAssetTypeId(item?.assetType) === ASSET_TYPES.MOTOR;
    const isAnomalyAssetType = isMotorAsset || isDriveAsset;
    const hasAnomalyDetection = isAnomalyAssetType && getAnomalyDetectionKPI?.anomalyLevel > 0;


    // For drives: count all anomaly KPIs that are actively anomalous
    const driveAnomalyCount = isDriveAsset
        ? item?.trend?.KPIs?.filter(kpi => kpi?.isAnomaly && kpi?.anomalyLevel > 0)?.length ?? 0
        : 0;
    const hasDriveAnomalyDetection = isDriveAsset && driveAnomalyCount > 0;

    const getConditionData = (groupOfKPIs, condition, isTrend = false, iconCondition) => {
        const getIcon = () => {
            if (newConditionIndices[condition]?.iconID !== undefined && newConditionIndices[condition]?.iconID !== null) {
                if (
                    isTrend &&
                    hasAnomalyDetection
                ) {
                    return <IconAnomalyDetection />;
                } else if (isTrend && iconCondition === conditionIndices.POOR) {
                    return <IconTrendPoorIndicator />;
                } else if (isTrend && iconCondition === conditionIndices.TOLERABLE) {
                    return <IconTrendTolerableIndicator />;
                } else {
                    return <IconCondition condition={newConditionIndices[iconCondition].iconID} />;
                }
            } else {
                return null;
            }
        };
        return {
            count: groupOfKPIs[condition]?.length || 0,
            icon: getIcon()
        };
    };

    // Memoize grouped KPIs to avoid recalculating on every render
    const filteredKPIs = () => item?.trend?.KPIs?.filter(kpi => !kpi.isAnomaly);
    const groupOfKPIs = useMemo(() => groupBy(filteredKPIs(), 'condition'), [item?.trend?.KPIs]);
    const kpiList = item?.trend?.KPIs;
    const selectedCondition = conditions.find(condition => groupOfKPIs[condition]?.length > 0) || null;
    const { count, icon } = selectedCondition ? getConditionData(groupOfKPIs, selectedCondition, true, item?.trend?.condition) : { count: null, icon: '' };
    const trendIcon = hasAnomalyDetection && !isDriveAsset ? <IconAnomalyDetection /> : icon;

    // Show count only for POOR and TOLERABLE conditions
    const shouldShowCount = count && (selectedCondition === conditionIndices.POOR || selectedCondition === conditionIndices.TOLERABLE);

    const isLeftPanelSmall = leftPanelSize <= 50;

    const renderKPIs = () => {
        return kpiList && kpiList?.length > 0 ?
            <div className='render-condition'>
                <div className={isLeftPanelSmall ? 'left-panel-small' : ''}>
                    {hasDriveAnomalyDetection ? <IconAnomalyDetection /> : trendIcon}
                </div>
                {/* Drive: always show anomaly count; Motor/others: show CI count for poor/tolerable */}
                {hasDriveAnomalyDetection && isLabelVisible &&
                    <div className='kpis'>
                        {isLeftPanelSmall ? null : driveAnomalyCount}
                    </div>
                }
                {!hasDriveAnomalyDetection && shouldShowCount && isLabelVisible &&
                    <div className='kpis'>
                        {!isLeftPanelSmall ? `${count} ${count === 1 ?
                            translate('ABB.Powertrain.Frontend.conditionOverviewConditionIndicesLabel') :
                            translate('ABB.Powertrain.Frontend.conditionOverviewConditionIndicesLabels')}` : null}
                    </div>
                }
            </div> :
            item?.trend?.condition === conditionIndices.UNKNOWN && <IconCondition condition={newConditionIndices[conditionIndices.UNKNOWN].iconID} />;

    };

    // Memoize the rendering of trend conditions
    const renderTrendConditions = useMemo(() => {
        return (
            <Tippy
                content={
                    <div className='threshold-tooltip'>
                        <span>{translate('ABB.Powertrain.Frontend.conditionOverviewWithAlarmLabel')} <b>{groupOfKPIs[conditionIndices.POOR]?.length || 0}</b></span>
                        <span>{translate('ABB.Powertrain.Frontend.conditionOverviewWithAlertLabel')} <b>{groupOfKPIs[conditionIndices.TOLERABLE]?.length || 0}</b></span>
                        {hasDriveAnomalyDetection &&
                            <span>{translate('ABB.Powertrain.Frontend.conditionOverviewWithAnomalyLabel')} <b>{driveAnomalyCount}</b></span>
                        }
                        {hasAnomalyDetection && !isDriveAsset &&
                            <span>{translate('ABB.Powertrain.Frontend.conditionOverviewAnomalyDetected')}</span>
                        }
                    </div>
                }
                interactive
                interactiveBorder={10}
                animation='scale'
                theme='light-border'
                trigger='mouseenter'
                maxWidth={450}
                placement='right'
                offset='-85,0'
            >
                <div>
                    {renderKPIs()}
                </div>
            </Tippy>
        );
    }, [groupOfKPIs]);

    return (
        <div className={`condition-index trend  ${isLeftPanelSmall ? 'left-sidebar-cell' : 'cell'}`}>
            {renderTrendConditions}
        </div>
    );
});

TrendViewComponent.propTypes = {
    item: PropTypes.shape({
        assetType: PropTypes.string,
        trend: PropTypes.shape({
            KPIs: PropTypes.arrayOf(PropTypes.object),
            condition: PropTypes.string
        })
    }).isRequired,
    isLabelVisible: PropTypes.bool.isRequired,
    leftPanelSize: PropTypes.number.isRequired
};

export default TrendViewComponent;
